import { OralExamMode } from '@/common/api/models/oralExamMode';

/** Who may start the exam session. Matches EventKind.start_authority on the wire. */
export type OralExamStartAuthority = 'lecturer' | 'self_service';

/**
 * Run-state fields that decide conduct UI. Generated orval types may lag the
 * live schema; callers pass the poll payload and these readers default safely.
 */
export type RunConductFields = {
  mode?: OralExamMode | string;
  capacity?: number;
  startAuthority?: OralExamStartAuthority | string;
  requiresEvaluation?: boolean;
};

export function runStartAuthority(run: RunConductFields | null | undefined): OralExamStartAuthority {
  return run?.startAuthority === 'self_service' ? 'self_service' : 'lecturer';
}

export function runRequiresEvaluation(run: RunConductFields | null | undefined): boolean {
  return run?.requiresEvaluation !== false;
}

export function isSelfServiceRun(run: RunConductFields | null | undefined): boolean {
  return runStartAuthority(run) === 'self_service';
}

/** Agent-conducted exam surface (orb / 1:N shell). Trust `mode`; do not guess from capacity. */
export function isCohortExamSurface(run: RunConductFields | null | undefined): boolean {
  return run?.mode === OralExamMode.one_to_n;
}

/** Lecturer-conducted exam surface. Trust `mode`; do not guess from capacity. */
export function isOneToOneExamSurface(run: RunConductFields | null | undefined): boolean {
  return run?.mode === OralExamMode.one_to_one;
}

export function isOralExamRunStateTerminal(state: string | undefined | null): boolean {
  return state === 'completed' || state === 'aborted';
}

/** True when End exam would cut off an exam session still in progress. */
export function hasInProgressOralExamRuns(
  bookingRuns: ReadonlyArray<{ runState?: { state?: string | null } | null }>,
): boolean {
  return bookingRuns.some((run) => run.runState?.state === 'in_progress');
}

/**
 * Tenant 1:N briefing film. Plays on the cohort surface, including
 * self-service dry-run. Not the shared lecturer lounge.
 */
export function usesOrientationFilm(run: RunConductFields | null | undefined): boolean {
  return isCohortExamSurface(run);
}

/**
 * Shared group briefing lounge (wait-for-lecturer / lecturer-opened room).
 * Lecturer-started 1:N only — never self-service. The film itself is
 * {@link usesOrientationFilm}.
 */
export function usesGroupLounge(run: RunConductFields | null | undefined): boolean {
  return isCohortExamSurface(run) && runStartAuthority(run) === 'lecturer';
}

/**
 * Asked-question count for the live progress strip.
 *
 * Students never receive exam session `selectedQuestionIds` (empty list). The
 * server sends `questionCount` instead. Lecturers still have IDs; fall back
 * to `.length` when `questionCount` is absent (pre-orval mocks).
 */
export function oralExamAskedQuestionCount(
  runState: { selectedQuestionIds?: string[]; questionCount?: number } | null | undefined,
): number {
  if (typeof runState?.questionCount === 'number' && runState.questionCount > 0) {
    return runState.questionCount;
  }
  return runState?.selectedQuestionIds?.length ?? 0;
}
